import { Link, NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  FileStack,
  Image as ImageIcon,
  Layers,
  Mail,
  Briefcase,
  FolderKanban,
  Newspaper,
  Menu,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
} from 'lucide-react';
import DashboardLogo from './DashboardLogo';

const navItems = [
  { to: '/dashboard', end: true, label: 'Dashboard', icon: LayoutDashboard },
  { to: '/dashboard/menus', label: 'Menus', icon: Menu },
  { to: '/dashboard/pages', label: 'Pages Manager', icon: FileStack },
  { to: '/dashboard/content', label: 'Dynamic Content', icon: Layers },
  { to: '/dashboard/media', label: 'Media Manager', icon: ImageIcon },
  { to: '/dashboard/contacts', label: 'Contacts', icon: Mail },
  { to: '/dashboard/services', label: 'Services', icon: Briefcase },
  { to: '/dashboard/projects', label: 'Projects', icon: FolderKanban },
  { to: '/dashboard/blogs', label: 'Blogs', icon: Newspaper },
];

function navLinkClass(expanded, isActive) {
  const base =
    'flex items-center rounded-xl text-sm font-medium text-white/75 transition hover:bg-white/10 hover:text-white';
  const layout = expanded
    ? 'gap-3 px-3 py-2.5'
    : 'justify-center px-2 py-2.5 lg:w-full';
  const active = isActive
    ? 'bg-[#DF9633] text-[#191B1C] hover:bg-[#DF9633] hover:text-[#191B1C]'
    : '';
  return `${base} ${layout} ${active}`;
}

export default function Sidebar({ expanded, onToggle }) {
  return (
    <aside
      className={`flex shrink-0 flex-col border-b border-white/25 bg-[#191B1C] transition-[width] duration-300 ease-in-out lg:min-h-screen lg:border-b-0 lg:border-r ${
        expanded ? 'w-full lg:w-64' : 'w-full lg:w-[4.5rem]'
      }`}
    >
      <div
        className={`flex border-b border-white/25 ${
          expanded
            ? 'items-center justify-between gap-2 px-4 py-4 lg:px-5'
            : 'flex-col items-center gap-3 px-2 py-4'
        }`}
      >
        <Link
          to="/dashboard"
          title="Tevris dashboard"
          className="inline-flex shrink-0 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[#DF9633]"
        >
          <DashboardLogo
            className={
              expanded
                ? 'h-9 w-auto max-w-[160px]'
                : 'h-8 w-8 object-contain object-left'
            }
          />
        </Link>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-label={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/25 text-white/80 transition hover:border-white/50 hover:bg-white/10 hover:text-white"
        >
          {expanded ? (
            <ChevronLeft className="h-5 w-5" aria-hidden />
          ) : (
            <ChevronRight className="h-5 w-5" aria-hidden />
          )}
        </button>
      </div>

      <nav
        className={`flex gap-1 p-2 ${
          expanded
            ? 'max-h-36 flex-1 flex-row flex-wrap overflow-y-auto lg:max-h-none lg:flex-col lg:justify-start lg:p-3'
            : 'shrink-0 flex-row justify-center lg:flex-col lg:items-stretch lg:justify-start lg:overflow-visible lg:p-2'
        }`}
        aria-label="Dashboard"
      >
        {navItems.map(({ to, end, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            title={!expanded ? label : undefined}
            className={({ isActive }) => navLinkClass(expanded, isActive)}
          >
            <Icon className="h-5 w-5 shrink-0" aria-hidden />
            {expanded ? <span className="truncate">{label}</span> : null}
          </NavLink>
        ))}
      </nav>

      <div
        className={`shrink-0 border-t border-white/25 ${
          expanded ? 'p-3 lg:p-4' : 'mt-auto flex justify-center p-2 lg:p-3'
        }`}
      >
        <Link
          to="/"
          title="Back to public site"
          className={`inline-flex items-center text-sm font-medium text-white/70 transition hover:text-white ${
            expanded
              ? 'gap-2 underline-offset-4 hover:underline'
              : 'justify-center rounded-lg p-2.5 hover:bg-white/10'
          }`}
        >
          <ArrowLeft className="h-5 w-5 shrink-0" aria-hidden />
          {expanded ? <span>Back to site</span> : null}
        </Link>
      </div>
    </aside>
  );
}
